import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, Router } from '@angular/router';
import { AsyncQueue } from '@sapphire/async-queue';
import qs from 'qs';
import { dsLogger } from '../logger/logger.instance';

const logger = dsLogger.extend('querify-service');

type IsArray<T> = T extends unknown[] ? true : false;

/**
 * Transforma recursivamente um tipo para representar a estrutura dos parâmetros de query após a "inflação".
 * Arrays são preservados, objetos são processados recursivamente e primitivos se tornam strings.
 */
export type ParamsResponse<T> = {
  [K in keyof T]: T[K] extends Record<string, unknown>
    ? ParamsResponse<T[K]>
    : IsArray<T[K]> extends true
      ? T[K]
      : string;
};

@Injectable({
  providedIn: 'root',
})
export class QuerifyService {
  /**
   * Instância do Angular Router.
   */
  private router = inject(Router);

  /**
   * Armazenamento interno dos parâmetros atuais.
   */
  private _params: Record<string, unknown> = {};

  /**
   * Fila para serializar operações de atualização de parâmetros.
   */
  private asyncQueue = new AsyncQueue();

  /**
   * Inscreve-se nos eventos do router para manter os parâmetros internos sincronizados com a navegação.
   */
  public constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this._params = this.router.parseUrl(event.url).queryParams;
      }

      if (event instanceof NavigationCancel) {
        logger.warn(
          `Detected NavigationCancel event. This may be caused by a race condition within the application. (Reason: ${event.reason})`,
          event,
        );
      }
    });
  }

  /**
   * Verifica se existem parâmetros de query presentes.
   *
   * @returns True se existir algum parâmetro, false caso contrário.
   */
  public hasParams(): boolean {
    return Object.keys(this.getParams()).length > 0;
  }

  /**
   * Define os parâmetros de query, substituindo todos os existentes.
   *
   * @param params - Parâmetros a serem definidos.
   */
  public async setParams<T extends Record<string, unknown>>(params: T): Promise<void> {
    await this.asyncQueue.wait();
    await this._setParams(params);
  }

  /**
   * Método interno para definir os parâmetros e atualizar o router.
   *
   * @param params - Parâmetros a serem definidos.
   */
  private async _setParams(params: Record<string, unknown>): Promise<void> {
    const deflateParams = this.deflateParams(params);
    this._params = params;

    await this.router
      .navigate([], {
        queryParams: deflateParams,
      })
      .finally(() => {
        this.asyncQueue.shift();
      });
  }

  /**
   * Obtém os parâmetros de query atuais, "inflados" para sua estrutura original.
   *
   * @returns Objeto de parâmetros inflado.
   */
  public getParams<T extends Record<string, unknown>>(): ParamsResponse<T> {
    const deflateParams = this.router.parseUrl(this.router.url).queryParams;

    return this.inflateParams(deflateParams) as ParamsResponse<T>;
  }

  /**
   * Adiciona ou mescla novos parâmetros aos parâmetros de query atuais.
   *
   * @param params - Parâmetros a serem adicionados ou atualizados.
   */
  public async addParams(params: Record<string, unknown>): Promise<void> {
    await this.asyncQueue.wait();
    await this._addParams(params);
  }

  /**
   * Método interno para mesclar parâmetros e atualizar o router.
   *
   * @param params - Parâmetros a serem adicionados.
   */
  private async _addParams(params: Record<string, unknown>): Promise<void> {
    const currentParams = this.getParams();

    this._params = {
      ...currentParams,
      ...this._params,
      ...params,
    };

    const deflateParams = this.deflateParams(this._params);

    await this.router
      .navigate([], {
        queryParams: deflateParams,
        queryParamsHandling: 'merge',
      })
      .finally(() => {
        this.asyncQueue.shift();
      });
  }

  /**
   * Remove os parâmetros especificados dos parâmetros de query.
   *
   * @param params - Array de chaves de parâmetros a serem removidas.
   */
  public async removeParams(params: string[]): Promise<void> {
    await this.asyncQueue.wait();
    await this._removeParams(params);
  }

  /**
   * Método interno para remover parâmetros e atualizar o router.
   *
   * @param params - Chaves a serem removidas.
   */
  private async _removeParams(params: string[]): Promise<void> {
    const currentParams = this.getParams();

    for (const param of params) {
      Reflect.deleteProperty(currentParams, param);
      Reflect.deleteProperty(this._params, param);
    }

    await this._setParams(currentParams);
  }

  /**
   * Limpa todos os parâmetros de query.
   */
  public async clearParams(): Promise<void> {
    await this.asyncQueue.wait();
    await this._clearParams();
  }

  /**
   * Método interno para limpar todos os parâmetros e atualizar o router.
   */
  private async _clearParams(): Promise<void> {
    this._params = {};
    await this.router
      .navigate([], {
        queryParams: {},
      })
      .finally(() => {
        this.asyncQueue.shift();
      });
  }

  /**
   * @param params - Os parâmetros a serem achatados.
   * @returns Um objeto achatado adequado para parâmetros de consulta.
   */
  public deflateParams(params: Record<string, unknown>): Record<string, string> {
    const serializedParams = qs.stringify(params, {
      encode: false,
      allowDots: true,
      arrayFormat: 'indices',
      skipNulls: true,
      allowEmptyArrays: true,
    });

    return Object.fromEntries(new URLSearchParams(serializedParams).entries());
  }

  /**
   * @param params - Flat parameters from a query string.
   * @returns The inflated parameters object.
   */
  public inflateParams(params: Record<string, unknown>): Record<string, any> {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      searchParams.append(key, value as string);
    }

    const serializedParams = searchParams.toString();
    return qs.parse(serializedParams, {
      allowDots: true,
      arrayLimit: 100,
      allowEmptyArrays: true,
    });
  }
}
